import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import { KIOSK_PATH_PREFIX } from '@/constants';

/**
 * TV / kiosk display mode for a page. The layout hides chrome (header, drawer,
 * padding) and the page can opt into native browser fullscreen.
 *
 * Active when ANY of these hold:
 *   - URL path starts with /kiosk/  (dedicated kiosk routes use KioskLayout)
 *   - query `kiosk=1`               (in-app toggle for the normal routes)
 *   - query `fullscreen=true`       (legacy alias for the ColleaguesPage button)
 *
 * This composable does NOT auto-refresh page data — pages own their own
 * intervals and watch `isKioskActive` themselves.
 */
export const useTvMode = () => {
  const route = useRoute();
  const router = useRouter();
  const $q = useQuasar();

  const isKioskActive = computed(
    () => route.path.startsWith(KIOSK_PATH_PREFIX) || route.query.kiosk === '1' || route.query.fullscreen === 'true',
  );

  const setKioskQuery = (active: boolean) => {
    const nextQuery = { ...route.query };
    if (active) {
      nextQuery.kiosk = '1';
    } else {
      delete nextQuery.kiosk;
      delete nextQuery.fullscreen;
    }
    return router.replace({ query: nextQuery });
  };

  const toggleKiosk = () => setKioskQuery(!isKioskActive.value);

  const requestNativeFullscreen = () => {
    if (!$q.fullscreen.isActive) {
      $q.fullscreen.request().catch(() => {
        // Browser requires user-initiated gesture; swallow and continue.
      });
    }
  };

  const exitNativeFullscreen = () => {
    if ($q.fullscreen.isActive) {
      $q.fullscreen.exit().catch(() => {});
    }
  };

  return {
    isKioskActive,
    toggleKiosk,
    setKioskQuery,
    requestNativeFullscreen,
    exitNativeFullscreen,
  };
};
